import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { FiArrowLeft, FiPrinter, FiShare2 } from 'react-icons/fi';
import { getCRByNumber, getChurchProfile } from '../../services/api';
import { printCashReceipt, shareCashReceipt, prepareCashReceiptShare } from '../../utils/printReceipt';
import Toast from '../../components/Toast';

const buildReceiptPayload = (c) => ({
  cr_number: c.cr_number,
  date: c.date,
  received_from: c.member_name?.trim() || c.received_from || '',
  member_card: c.member_card || '',
  spiritual_father_name: c.spiritual_father_name || '',
  hagest_sbket: c.hagest_sbket || '',
  city: c.city || '',
  fund_name: c.fund_name || '',
  payment_method: c.payment_method || '',
  amounts: c.amounts || {},
  notes: c.notes || '',
  received_by_name: c.created_by_name || '',
  verify_token: c.verify_token || '',
});

const crStatusPill = { pending: 'pill-yellow', approved: 'pill-green', rejected: 'pill-red' };

// Read-only view of a single Cash Receipt. Reached from the CR list
// (click the CR-XXXX cell or the 👁 View action) and from any member's
// contribution table (click their CR-XXXX pill).
const CRView = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { crNumber } = useParams();
  const navigate = useNavigate();
  const [cr, setCr] = useState(null);
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => setToast({ msg, type });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: crData }, { data: profData }] = await Promise.all([
          getCRByNumber(crNumber),
          getChurchProfile(),
        ]);
        if (!alive) return;
        setCr(crData);
        setProfile(profData || {});
        // Build the share PDF now so tapping Share opens the sheet instantly.
        prepareCashReceiptShare(buildReceiptPayload(crData), profData || {});
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load receipt');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [crNumber]);

  const handlePrint = () => {
    try { printCashReceipt(buildReceiptPayload(cr), profile); }
    catch (e) { showToast(e?.message || 'Print failed', 'error'); }
  };

  const handleShare = async () => {
    try {
      showToast('Preparing receipt to share…');
      const res = await shareCashReceipt(buildReceiptPayload(cr), profile);
      if (res?.shared === 'downloaded') showToast(`PDF downloaded (${res.filename})`);
      else if (res?.shared === 'needs-tap') showToast('Receipt ready — tap Share again');
      else setToast(null);
    } catch (e) {
      showToast(e?.message || 'Could not share the receipt', 'error');
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading receipt…</div>;
  }
  if (error || !cr) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠</div>
        <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: 16 }}>{error || 'Receipt not found'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/finance/cr')}>
          <FiArrowLeft /> Back to Cash Receipts
        </button>
      </div>
    );
  }

  const st = cr.status || 'approved';
  const amounts = cr.amounts || {};
  const currencies = Object.keys(amounts).filter(sym => parseFloat(amounts[sym]) > 0);
  const verifyUrl = cr.verify_token ? `${window.location.origin}/verify/${cr.verify_token}` : '';
  const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const rowStyle = { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f3f4f6' };
  const labelStyle = { fontSize: 13, color: '#6b7280', fontWeight: 500 };
  const valueStyle = { fontSize: 14, color: '#111827', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' };

  return (
    <div style={{ padding: '20px 24px', maxWidth: 780, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
          <FiArrowLeft /> {t('common.back', { defaultValue: 'Back' })}
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isMobile && <button className="btn btn-secondary" onClick={handlePrint}><FiPrinter /> {t('recordAttendance.print', { defaultValue: 'Print' })}</button>}
          <button className="btn btn-primary" onClick={handleShare}><FiShare2 /> Share</button>
        </div>
      </div>

      {/* Card */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '28px 32px', border: '1px solid #e5e7eb' }}>
        {/* Title row */}
        <div style={{ borderBottom: '2px solid #f3f4f6', paddingBottom: 20, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>
                {t('finance.crNumber', { defaultValue: 'CR No.' })}
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', fontFamily: 'monospace', letterSpacing: 1 }}>
                {cr.cr_number}
              </div>
            </div>
            <span className={`pill ${crStatusPill[st] || 'pill-gray'}`} style={{ fontSize: 13, padding: '6px 14px' }}>{st}</span>
          </div>
        </div>

        {/* Fields */}
        <div style={rowStyle}>
          <span style={labelStyle}>{t('common.date', { defaultValue: 'Date' })}</span>
          <span style={valueStyle}>
            {cr.date}
            {(cr.approved_at || cr.created_at) && (
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginLeft: 8 }}>
                {String(cr.approved_at || cr.created_at).slice(11, 16)}
              </span>
            )}
          </span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t('finance.receivedFrom', { defaultValue: 'Received From' })}</span>
          <span style={valueStyle}>{cr.member_name?.trim() || cr.received_from || '—'}</span>
        </div>
        {cr.spiritual_father_name && (
          <div style={rowStyle}>
            <span style={labelStyle}>{t('members.spiritualFather', { defaultValue: 'Spiritual Father' })}</span>
            <span style={valueStyle}>{cr.spiritual_father_name}</span>
          </div>
        )}
        {cr.hagest_sbket && (
          <div style={rowStyle}>
            <span style={labelStyle}>{t('finance.diocese', { defaultValue: 'Diocese' })}</span>
            <span style={valueStyle}>{cr.hagest_sbket}</span>
          </div>
        )}
        {cr.city && (
          <div style={rowStyle}>
            <span style={labelStyle}>{t('finance.city', { defaultValue: 'City' })}</span>
            <span style={valueStyle}>{cr.city}</span>
          </div>
        )}
        <div style={rowStyle}>
          <span style={labelStyle}>{t('finance.fund', { defaultValue: 'Fund' })}</span>
          <span style={valueStyle}>{cr.fund_name || '—'}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t('finance.paymentMethod', { defaultValue: 'Method' })}</span>
          <span style={{ ...valueStyle, textTransform: 'capitalize' }}>{(cr.payment_method || '').replace(/_/g, ' ') || '—'}</span>
        </div>

        {/* Amounts — one big block per currency actually paid */}
        <div style={{ margin: '20px 0', padding: '16px 20px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Amount</div>
          {currencies.length === 0 ? (
            <div style={{ color: '#9ca3af' }}>—</div>
          ) : currencies.map(sym => (
            <div key={sym} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '4px 0' }}>
              <span style={{ fontSize: 14, color: '#374151' }}>{sym}</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>{sym}{fmt(amounts[sym])}</span>
            </div>
          ))}
        </div>

        <div style={rowStyle}>
          <span style={labelStyle}>{t('table.by', { defaultValue: 'Received By' })}</span>
          <span style={valueStyle}>{cr.created_by_name || '—'}</span>
        </div>
        {cr.approved_by_name && (
          <div style={rowStyle}>
            <span style={labelStyle}>{t('common.approvedBy', { defaultValue: 'Approved By' })}</span>
            <span style={valueStyle}>{cr.approved_by_name}</span>
          </div>
        )}
        {cr.notes && (
          <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
            <span style={labelStyle}>{t('common.notes', { defaultValue: 'Notes' })}</span>
            <span style={{ ...valueStyle, textAlign: 'left', maxWidth: '100%' }}>{cr.notes}</span>
          </div>
        )}
        {verifyUrl && (
          <div style={{ ...rowStyle, borderBottom: 'none', paddingTop: 14 }}>
            <span style={labelStyle}>Verify Link</span>
            <a href={verifyUrl} target="_blank" rel="noopener noreferrer" style={{ ...valueStyle, color: '#4f46e5', textDecoration: 'underline', fontWeight: 500, fontSize: 12 }}>
              {verifyUrl}
            </a>
          </div>
        )}
      </div>

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default CRView;
